import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OpenShift } from "../../../types/model/openShift";
import { OpenShiftStateType } from "../../../types/state/openShift";
import {
    getOpenShifts,
    claimOpenShifts,
    getMyClaimShiftRequestHistory,
    getOpenShiftDetails,
    getOpenShiftStatusCounts,
    cancelOpenShift,
} from "./api";
import { FULFILLED, PENDING, REJECTED } from "../../constants";

const initialState: OpenShiftStateType = {
    isLoadingOpenShifts: false,
    isLoadingOpenShiftDetails: false,
    isLoadingOpenShiftStatusCounts: false,
    openShifts: [],
    selectedOpenShift: undefined,
    selectedDate: undefined,
    selectedSourceType: undefined,
    isCancellingOpenShift: false,
    isCreatingOpenShift: false,
    isUpdatingOpenShift: false,
    isDeletingOpenShift: false,
    // New state for claim shift request history
    claimShiftRequestHistory: [],
    isLoadingClaimShiftRequestHistory: false,
    openShiftStatusCounts: undefined,
    error: undefined,
};

const openShiftSlice = createSlice({
    name: "openShift",
    initialState,
    reducers: {
        // Clear actions
        clearError: (state) => {
            state.error = undefined;
        },
        
        clearSelectedOpenShift: (state) => {
            state.selectedOpenShift = undefined;
        },
        
        clearSelectedOpenShiftDetail: (state) => {
            state.selectedOpenShiftDetail = undefined;
        },
        
        // Selection actions
        setSelectedOpenShift: (state, action: PayloadAction<OpenShift>) => {
            state.selectedOpenShift = action.payload;
        },
        
        setSelectedDate: (state, action: PayloadAction<Date>) => {
            state.selectedDate = action.payload;
        },
        
        setSelectedSourceType: (state, action: PayloadAction<string>) => {
            state.selectedSourceType = action.payload;
        },
        
        // Clear filters
        clearFilters: (state) => {
            state.selectedDate = undefined;
            state.selectedSourceType = undefined;
        },
    },
    extraReducers: (builder) => {
        // Get all open shifts
        builder
            .addCase(getOpenShifts.pending, (state) => {
                state.isLoadingOpenShifts = true;
                state.error = undefined;
            })
            .addCase(getOpenShifts.fulfilled, (state, action) => {
                state.isLoadingOpenShifts = false;
                state.openShifts = action.payload;
            })
            .addCase(getOpenShifts.rejected, (state, action) => {
                state.isLoadingOpenShifts = false;
                // Use action.payload when rejectWithValue is used, fallback to action.error.message
                state.error = (action.payload as string) || action.error.message;
            });

        // Claim open shifts
        builder
            .addCase(claimOpenShifts.pending, (state) => {
                state.isCreatingOpenShift = true;
                state.error = undefined;
            })
            .addCase(claimOpenShifts.fulfilled, (state) => {
                state.isCreatingOpenShift = false;
                // Clear selected shifts after successful claim
                state.selectedOpenShift = undefined;
            })
            .addCase(claimOpenShifts.rejected, (state, action) => {
                state.isCreatingOpenShift = false;
                state.error = (action.payload as string) || action.error.message;
            });

        // Get claim shift request history
        builder
            .addCase(getMyClaimShiftRequestHistory.pending, (state) => {
                state.isLoadingClaimShiftRequestHistory = true;
                state.error = undefined;
            })
            .addCase(getMyClaimShiftRequestHistory.fulfilled, (state, action) => {
                state.isLoadingClaimShiftRequestHistory = false;
                state.claimShiftRequestHistory = action.payload;
            })
            .addCase(getMyClaimShiftRequestHistory.rejected, (state, action) => {
                state.isLoadingClaimShiftRequestHistory = false;
                state.error = (action.payload as string) || action.error.message;
            });

        // Get open shift details
        builder
            .addCase(getOpenShiftDetails.pending, (state) => {
                state.isLoadingOpenShiftDetails = true;
                state.error = undefined;
            })
            .addCase(getOpenShiftDetails.fulfilled, (state, action) => {
                state.isLoadingOpenShiftDetails = false;
                state.selectedOpenShiftDetail = action.payload;
            })
            .addCase(getOpenShiftDetails.rejected, (state, action) => {
                state.isLoadingOpenShiftDetails = false;
                state.error = (action.payload as string) || action.error.message;
            });

        // Manager: cancel open shift
        builder
            .addCase(cancelOpenShift.pending, (state) => {
                state.isCancellingOpenShift = true;
                state.error = undefined;
            })
            .addCase(cancelOpenShift.fulfilled, (state) => {
                state.isCancellingOpenShift = false;
            })
            .addCase(cancelOpenShift.rejected, (state, action) => {
                state.isCancellingOpenShift = false;
                state.error = (action.payload as string) || action.error.message;
            });

        builder
            .addCase(getOpenShiftStatusCounts.pending, (state) => {
                state.isLoadingOpenShiftStatusCounts = true;
                state.error = undefined;
            })
            .addCase(getOpenShiftStatusCounts.fulfilled, (state, action) => {
                state.isLoadingOpenShiftStatusCounts = false;
                state.openShiftStatusCounts = action.payload;
            })
            .addCase(getOpenShiftStatusCounts.rejected, (state, action) => {
                state.isLoadingOpenShiftStatusCounts = false;
                state.error = (action.payload as string) || action.error.message;
            });
    },
});

export const {
    clearError,
    clearSelectedOpenShift,
    clearSelectedOpenShiftDetail,
    setSelectedOpenShift,
    setSelectedDate,
    setSelectedSourceType,
    clearFilters,
} = openShiftSlice.actions;

export default openShiftSlice.reducer;
